import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Camera, Play, ChevronDown, ChevronUp } from 'lucide-react';
import { useUserTasks } from '../../hooks/useTasks';
import { riskCalculationService } from '../../services/riskCalculationService';
import './ExecutionTasksTab.css';

/**
 * Tasks tab (PRD: execution_account_prd).
 * Tap task -> open steps/actions screen (ExecutionTaskExecutionView). Start/Continue is on that screen.
 */
export default function ExecutionTasksTab({ userId, onStartOrContinueTask }) {
  const { t } = useTranslation(['execution', 'tasks']);
  const { tasks, loading, error, refresh } = useUserTasks(userId, {});
  const [pendingExpanded, setPendingExpanded] = useState(false);
  const [showQrScan, setShowQrScan] = useState(false);

  const inProgress = tasks.filter((x) => x.status === 'in_progress');
  const pending = tasks.filter(
    (x) =>
      x.status === 'assigned' ||
      x.status === 'acknowledged' ||
      x.status === 'pending_assignment',
  );
  const heroTask = inProgress[0] || pending[0];
  const otherPending = pending.filter((x) => x.id !== heroTask?.id);

  const getRiskLevel = (task) => {
    const rpn = task.risk_data?.max_rpn ?? task.baseRPN ?? 0;
    return riskCalculationService.getRiskLevel(rpn);
  };

  const getRiskLabel = (task) => {
    const level = getRiskLevel(task);
    if (level === 'critical') return t('tasks:criticalRisk', 'RISC CRITIC');
    if (level === 'high') return t('tasks:highRisk', 'Risc ridicat');
    return t('tasks:mediumRisk', 'Risc mediu');
  };

  const getLocationLabel = (task) => {
    const ctx = task.context || {};
    if (ctx.roomName || ctx.departmentName) {
      return [ctx.departmentName, ctx.roomName].filter(Boolean).join(' → ');
    }
    return t('tasks:notSpecified', 'Nespecificat');
  };

  if (!userId) return null;

  return (
    <div className="execution-tasks-tab">
      <div className="execution-tasks-header">
        <h2 className="execution-tasks-title">{t('myTasks')}</h2>
        <button
          type="button"
          className="execution-qr-btn"
          onClick={() => setShowQrScan(true)}
          aria-label={t('scanQR', 'Scan QR')}
        >
          <Camera size={24} />
        </button>
      </div>

      {loading ? (
        <div className="execution-tasks-loading">
          <p>{t('tasks:loadingTasks', 'Se încarcă sarcinile...')}</p>
        </div>
      ) : error ? (
        <div className="execution-tasks-error">
          <p>{error}</p>
          <button type="button" className="btn btn-secondary" onClick={refresh}>
            {t('tasks:retry', 'Reîncearcă')}
          </button>
        </div>
      ) : !heroTask ? (
        <div className="execution-tasks-empty">
          <p>{t('noTasksThisShift', 'Nicio sarcină pentru tura curentă.')}</p>
        </div>
      ) : (
        <>
          <button
            type="button"
            className={`execution-hero-card risk-${getRiskLevel(heroTask) || 'medium'}`}
            onClick={() => onStartOrContinueTask(heroTask)}
          >
            <span className="execution-hero-risk">{getRiskLabel(heroTask)}</span>
            <span className="execution-hero-status">
              {heroTask.status === 'in_progress'
                ? t('inProgress', 'În progres')
                : t('pending', 'În așteptare')}
            </span>
            <h3 className="execution-hero-protocol">
              {heroTask.name || heroTask.protocols?.[0]?.protocolName || heroTask.id}
            </h3>
            <p className="execution-hero-location">{getLocationLabel(heroTask)}</p>
            <p className="execution-hero-meta">
              {heroTask.context?.estimatedDuration ?? 15} min
            </p>
          </button>

          {otherPending.length > 0 && (
            <div className="execution-pending-section">
              <button
                type="button"
                className="execution-pending-header"
                onClick={() => setPendingExpanded(!pendingExpanded)}
              >
                <span>
                  {otherPending.length} {t('tasksPending', 'sarcini în așteptare')}
                </span>
                {pendingExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </button>
              {pendingExpanded && (
                <div className="execution-pending-list">
                  {otherPending.map((task) => (
                    <button
                      key={task.id}
                      type="button"
                      className={`execution-pending-card risk-${getRiskLevel(task) || 'medium'}`}
                      onClick={() => onStartOrContinueTask(task)}
                    >
                      <div className="execution-pending-info">
                        <strong>
                          {task.name || task.protocols?.[0]?.protocolName}
                        </strong>
                        <span>{getLocationLabel(task)}</span>
                        <span>{task.context?.estimatedDuration ?? 15} min</span>
                      </div>
                      <span className="execution-pending-arrow">
                        <Play size={14} />
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {showQrScan && (
        <div className="execution-qr-overlay">
          <div className="execution-qr-modal">
            <h3>{t('scanQRTitle', 'Scan cod QR')}</h3>
            <p className="execution-qr-fallback">
              {t('scanQRFallback', 'Alege o sarcină din listă.')}
            </p>
            <div className="execution-qr-list">
              {(inProgress[0] ? [inProgress[0]] : [])
                .concat(pending)
                .map((task) => (
                  <button
                    key={task.id}
                    type="button"
                    className="execution-qr-list-item"
                    onClick={() => {
                      setShowQrScan(false);
                      onStartOrContinueTask(task);
                    }}
                  >
                    {task.name || task.protocols?.[0]?.protocolName} –{' '}
                    {getLocationLabel(task)}
                  </button>
                ))}
            </div>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowQrScan(false)}
            >
              {t('back', 'Înapoi')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
